import type React from 'react';

/* eslint-disable no-nested-ternary */
import { NotificationInline } from '@coop/components';
import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';

import { Helpers } from '../../../../utility/helpers';
import { parseTimeslotDate } from '../../../cnc/components/timeslotSelector/timeslotUtils';
import { useA11yAnnouncerDispatch } from '../../../common/hooks/useA11yAnnouncerDispatch';
import { useAppSelector } from '../../../common/hooks/useThunkDispatch';
import { cartSelectors } from '../../../common/selectors/cartSelectors';
import { storeSelectors } from '../../../common/selectors/storeSelectors';
import { DeliveryMode } from '../../../common/store/structureDefinitions/storeState';
import { ga4NavigationTracking } from '../../../common/tracking/ga4/navigation';
import { useIsB2bCrossContext } from '../../hooks/useIsB2bCrossContext';
import { useEarliestTimeslotFetch, useMaxOrderTimeslotExpiration } from './TimeslotPreview.hooks';
import styles from './TimeslotPreview.module.scss';

const TimeslotPreview = (props: {
    fullWidth?: boolean;
    className?: string;
    announceTimeslotExpiration?: boolean;
    onClick: () => void;
}) => {
    const earliestTimeslot = useEarliestTimeslotFetch();
    const isCartInitialized = useAppSelector(cartSelectors.isCartInitialized);
    const hasSelectedTimeslot = useAppSelector(cartSelectors.hasSelectedTimeslot);
    const { privateCartOnCompanySite } = useIsB2bCrossContext();
    const handleClick = () => {
        ga4NavigationTracking.deliveryTime.open();
        props.onClick();
    };

    if (!isCartInitialized) {
        return <TimeslotInvisibleSkeleton fullWidth={!!props.fullWidth} />;
    }

    return (
        <>
            {(() => {
                if (hasSelectedTimeslot && !privateCartOnCompanySite) {
                    return (
                        <TimeslotSelected
                            fullWidth={!!props.fullWidth}
                            onClick={handleClick}
                            className={props.className}
                            announceTimeslotExpiration={props.announceTimeslotExpiration}
                        />
                    );
                }

                if (earliestTimeslot) {
                    return (
                        <EarliestTimeslotAvailable
                            fullWidth={!!props.fullWidth}
                            startTime={earliestTimeslot.startTime}
                            endTime={earliestTimeslot.endTime}
                            onClick={handleClick}
                            className={props.className}
                        />
                    );
                }

                return (
                    <NoTimeslotsAvailable
                        fullWidth={!!props.fullWidth}
                        onClick={handleClick}
                        className={props.className}
                    />
                );
            })()}
        </>
    );
};

/** Exact visible lines + Ändra, spaced — browsers drop whitespace between flex/block children in content names. */
const accessibleNameFromVisibleLines = (...lines: string[]) => [...lines, 'Ändra'].join(' ');

const Container = ({
    fullWidth,
    theme,
    bottomslot,
    ariaLabelParts,
    ...props
}: React.PropsWithChildren<
    React.ComponentProps<'button'> & {
        fullWidth: boolean;
        theme: 'default' | 'green';
        bottomslot?: React.ReactNode;
        /** Visible label parts (same strings as on screen), excluding Ändra. */
        ariaLabelParts?: string[];
    }
>) => {
    return (
        <button
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
            type="button"
            aria-haspopup="dialog"
            aria-label={
                ariaLabelParts
                    ? accessibleNameFromVisibleLines(...ariaLabelParts)
                    : props['aria-label']
            }
            className={classNames(
                styles.Container,
                fullWidth && styles['Container--FullWidth'],
                theme === 'green' && styles['Container--Green'],
                props.className,
            )}
        >
            <div className={styles.Left}>{props.children}</div>
            <div className={styles.Right}>Ändra</div>
            {bottomslot && (
                <>
                    <div className={styles.Break} />
                    <div className={styles.Bottom}>{bottomslot}</div>
                </>
            )}
        </button>
    );
};

export const TimeslotInvisibleSkeleton = (props: { fullWidth: boolean }) => {
    return (
        <Container
            fullWidth={props.fullWidth}
            theme="default"
            style={{ visibility: 'hidden' }}
            aria-hidden
        >
            <span aria-hidden>Laddar</span>
            <span className={styles.Bold} aria-hidden>
                Laddar
            </span>
        </Container>
    );
};

export const NoStoreSelected = (props: {
    fullWidth?: boolean;
    onClick: () => void;
    className?: string;
}) => {
    return (
        <Container
            fullWidth={!!props.fullWidth}
            theme="green"
            onClick={props.onClick}
            className={props.className}
            ariaLabelParts={['Sortiment visas för:', 'Hemleverans i Stockholm']}
        >
            <span>Sortiment visas för:</span>
            <span className={styles.Bold}>Hemleverans i Stockholm</span>
        </Container>
    );
};

export const NoTimeslotsAvailable = (props: {
    fullWidth?: boolean;
    onClick: () => void;
    className?: string;
}) => {
    const deliveryMode = useAppSelector(storeSelectors.selectedDeliveryModeOrDefault);
    const postalCode = useAppSelector(cartSelectors.postalCode);

    const store = useAppSelector(storeSelectors.currentStore);
    const isPickup = deliveryMode === DeliveryMode.pickup;
    const pickupStoreName = isPickup && store ? Helpers.capitalizeWords(store.name) : null;
    const text = isPickup
        ? `Hämtas ${pickupStoreName ? `på ${pickupStoreName}` : ''}`
        : `Hemleverans${postalCode ? ` till ${postalCode}` : ''}`;
    return (
        <Container
            fullWidth={!!props.fullWidth}
            theme="default"
            onClick={props.onClick}
            className={props.className}
            ariaLabelParts={['Det finns inga lediga leveranstider', text]}
        >
            <span>Det finns inga lediga leveranstider</span>
            <span className={styles.Bold}>{text}</span>
        </Container>
    );
};

const TimeslotSelected = (props: {
    fullWidth: boolean;
    onClick: () => void;
    className?: string;
    announceTimeslotExpiration?: boolean;
}) => {
    const timeslot = useAppSelector((state) => state.cart.timeslot);
    const deliveryMode = useAppSelector(storeSelectors.selectedDeliveryModeOrDefault);
    const postalCode = useAppSelector(cartSelectors.postalCode);
    const store = useAppSelector(storeSelectors.currentStore);
    const isEditOrderMode = useAppSelector(cartSelectors.isInEditOrder);
    const isPickup = deliveryMode === DeliveryMode.pickup;
    const pickupStoreName = isPickup && store ? Helpers.capitalizeWords(store.name) : null;
    const [hasAnnouncedTimeslotExpiration, setAnnouncedTimeslotExpiration] = useState(false);
    const {
        show: showTimeslotExpiration,
        maxOrderTime,
        expireTimerTriggered,
    } = useMaxOrderTimeslotExpiration();
    const announce = useA11yAnnouncerDispatch();

    const timeslotExpirationAriaText = useMemo(() => {
        return showTimeslotExpiration && maxOrderTime
            ? `Beställ senast klockan ${Helpers.formatHHmmAria(new Date(maxOrderTime))} för att behålla tiden,`
            : '';
    }, [maxOrderTime, showTimeslotExpiration]);

    useEffect(() => {
        if (
            !hasAnnouncedTimeslotExpiration &&
            props.announceTimeslotExpiration &&
            (expireTimerTriggered || showTimeslotExpiration)
        ) {
            setAnnouncedTimeslotExpiration(true);
            announce({
                ariaLive: 'assertive',
                text: timeslotExpirationAriaText,
                sender: 'TimeslotPreview',
            });
        }
    }, [
        props.announceTimeslotExpiration,
        expireTimerTriggered,
        showTimeslotExpiration,
        timeslotExpirationAriaText,
        announce,
        hasAnnouncedTimeslotExpiration,
    ]);

    if (!timeslot) {
        return null;
    }

    const formattedDate = parseTimeslotDate(timeslot.startTime, timeslot.endTime);

    const { timeslotDay, startHour, endHour } = formattedDate;

    const text = isPickup
        ? `Hämtas ${pickupStoreName ? `på ${pickupStoreName}` : ''}`
        : `Hemleverans${postalCode ? ` till ${postalCode}` : ''}`;
    const timeslotRange = [startHour, endHour].join('–');
    const timeslotText = `${timeslotDay} kl. `;
    const timeslotLine = `${timeslotDay} kl. ${timeslotRange}`;

    return (
        <Container
            fullWidth={props.fullWidth}
            theme="default"
            onClick={props.onClick}
            className={props.className}
            ariaLabelParts={[text, timeslotLine]}
            bottomslot={
                showTimeslotExpiration ? (
                    <NotificationInline
                        variant="critical"
                        show={showTimeslotExpiration}
                        animated={false}
                        aria-hidden
                        role="status"
                        message={
                            isEditOrderMode
                                ? `Du måste slutföra köpet senast kl. ${Helpers.formatHHmm(new Date(maxOrderTime || 0))} för att ändringar ska sparas.`
                                : `Beställ senast kl. ${Helpers.formatHHmm(new Date(maxOrderTime || 0))} för att behålla tiden.`
                        }
                    />
                ) : undefined
            }
        >
            <span>{text}</span>
            <span className={styles.Bold}>
                {timeslotText}
                <span className={styles.TimeslotTime}>{timeslotRange}</span>
            </span>
        </Container>
    );
};

const EarliestTimeslotAvailable = (props: {
    startTime: number;
    endTime: number;
    onClick: () => void;
    className?: string;
    fullWidth: boolean;
}) => {
    const deliveryMode = useAppSelector(storeSelectors.selectedDeliveryModeOrDefault);
    const isPickup = deliveryMode === DeliveryMode.pickup;

    const formattedDate = parseTimeslotDate(props.startTime, props.endTime);

    const { timeslotDay: timeSlotDay, startHour, endHour } = formattedDate;

    const text = isPickup ? 'Nästa lediga upphämtningstid' : 'Nästa lediga leveranstid';
    const timeslotRange = [startHour, endHour].join('–');
    const timeslotText = `${timeSlotDay} kl. `;
    const timeslotLine = `${timeSlotDay} kl. ${timeslotRange}`;

    return (
        <Container
            fullWidth={props.fullWidth}
            theme="default"
            onClick={props.onClick}
            className={props.className}
            ariaLabelParts={[text, timeslotLine]}
            data-testid="timeslot-select"
        >
            <span>{text}</span>
            <span className={styles.Bold}>
                {timeslotText}
                <span className={styles.TimeslotTime}>{timeslotRange}</span>
            </span>
        </Container>
    );
};

export default TimeslotPreview;
